/**
 * Document provenance helpers.
 *
 * Every instrument an Association generates is that Association's own paper.
 * It carries the Association's identity and nothing else: no portal branding
 * in the body, the footer, the HTML title, or the saved file name.
 */

/** Lowercase, underscore separated, letters and numbers only. */
export function underscoreSlug(value?: string | null, maxLength?: number): string {
  let slug = String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (maxLength) slug = slug.slice(0, maxLength).replace(/_+$/g, '');
  return slug;
}

/** Lowercase, underscored, letters and numbers only, trimmed to 40 characters. */
export function entitySlug(name?: string | null): string {
  return underscoreSlug(name, 40) || 'association';
}

/** Short, stable tail taken from a task or record id. */
export function shortId(id?: string | null): string {
  const clean = String(id ?? '').replace(/[^a-z0-9]/gi, '');
  return (clean.slice(-8) || 'document').toLowerCase();
}

/** Current local date as YYYYMMDD, zero padded, so names sort chronologically. */
export function dateStamp(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

/**
 * Storage path for a generated instrument, so a downloaded file reads as the
 * entity's own paper: {entity_slug}_{YYYYMMDD}_{document_key}_{shortid}.pdf
 */
export function generatedFilePath(entityName: string | null | undefined, documentKey: string, id: string): string {
  const key = underscoreSlug(documentKey) || 'document';
  return `generated/${entitySlug(entityName)}_${dateStamp()}_${key}_${shortId(id)}.pdf`;
}

/**
 * Browser download filename for a generated instrument. Titles like
 * "Acknowledging Resolution, Entity Name" can never leak a comma.
 */
export function downloadFileName(documentName?: string | null, suffix = ''): string {
  const base = underscoreSlug(documentName) || 'document';
  const tail = underscoreSlug(suffix);
  return `${base}_${dateStamp()}${tail ? `_${tail}` : ''}.pdf`;
}


function escapeTitle(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Rewrites the HTML <title> to "Document title, Entity name" so the PDF
 * viewer, the browser tab, and the PDF title metadata all name the entity.
 */
export function withEntityTitle(html: string, entityName?: string | null): string {
  const entity = String(entityName ?? '').trim();
  if (!entity) return html;
  return html.replace(/<title>([\s\S]*?)<\/title>/i, (_match, current: string) => {
    const docTitle = current.trim();
    if (!docTitle) return `<title>${escapeTitle(entity)}</title>`;
    if (docTitle.toLowerCase().includes(entity.toLowerCase())) return `<title>${docTitle}</title>`;
    return `<title>${docTitle}, ${escapeTitle(entity)}</title>`;
  });
}
